import React from "react";
import { Link, useParams } from "react-router-dom";
import {
  ACTIVITIES,
  ACTIVITY_PHASES,
  ActivityId,
  CAMERA_SETUPS,
  DRILLS,
  METRICS,
  METRICS_BY_FAMILY,
  angleFromActivity,
  activityFamily,
} from "../mockData";
import {
  ArrowLeft,
  ChevronRight,
  Camera,
  Dumbbell,
  Sparkles,
  Send,
  Clock,
  Activity,
} from "lucide-react";

export default function SportDetail() {
  const { activityId } = useParams<{ activityId: string }>();
  const sport = ACTIVITIES.find((s) => s.id === activityId);

  if (!sport) {
    return (
      <div className="card p-8 text-center text-slate-500">
        Activity not found.{" "}
        <Link to="/sports" className="text-el-700">Back to library</Link>
      </div>
    );
  }

  const phases  = ACTIVITY_PHASES[sport.id as ActivityId] ?? [];
  const angle   = angleFromActivity(sport.id as ActivityId);
  const family  = activityFamily(sport.id as ActivityId);

  // related drills — activity family match (golf drills surface for both face-on + DTL)
  const drills  = DRILLS.filter((d) => d.activity === family);
  // camera setup — match sport label or family
  const setups  = CAMERA_SETUPS.filter(
    (s) => s.activity.toLowerCase() === family.toLowerCase() || s.activity.toLowerCase() === sport.label.split(" — ")[0].toLowerCase()
  );
  // metrics relevant to this activity
  const allowed = new Set(METRICS_BY_FAMILY[family] ?? []);
  const metrics = METRICS.filter((m) => allowed.has(m.key));

  return (
    <div className="space-y-4">
      {/* breadcrumb */}
      <div className="flex items-center gap-3 text-sm text-slate-500">
        <Link to="/sports" className="inline-flex items-center gap-1 hover:text-slate-700">
          <ArrowLeft className="h-3.5 w-3.5" /> Library
        </Link>
        <ChevronRight className="h-3.5 w-3.5 text-slate-300" />
        <span className="text-slate-700">{family}</span>
        <ChevronRight className="h-3.5 w-3.5 text-slate-300" />
        <span className="text-slate-700 font-medium">{sport.label}</span>
      </div>

      {/* hero */}
      <div className="card p-6">
        <div className="flex items-start gap-4 flex-wrap">
          <div className="h-14 w-14 rounded-lg bg-gradient-to-br from-el-400 to-el-700 text-white text-3xl flex items-center justify-center">
            {sport.emoji}
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <h2 className="text-xl font-semibold tracking-tightish text-slate-900">{sport.label}</h2>
              <span className="badge bg-el-50 text-el-700 ring-1 ring-el-200">{family}</span>
              <span className="badge bg-slate-100 text-slate-600 ring-1 ring-slate-200">{angle}</span>
              <span className="badge bg-slate-100 text-slate-600 ring-1 ring-slate-200">
                {sport.positions} key positions
              </span>
            </div>
            <p className="mt-1 text-sm text-slate-600 max-w-2xl">
              The {family.toLowerCase()} template captured at the <span className="font-medium text-slate-800">{angle.toLowerCase()}</span> angle.
              Eleetz auto-detects every named position and emits the body-measure stream behind
              every report.
            </p>
          </div>
          <div className="flex items-center gap-2">
            <button className="btn-secondary">
              <Send className="h-4 w-4" /> Assign to roster
            </button>
            <button className="btn-primary">
              <Activity className="h-4 w-4" /> Capture now
            </button>
          </div>
        </div>
      </div>

      {/* Phase timeline */}
      <div className="card p-5">
        <div className="flex items-center justify-between mb-3">
          <div>
            <div className="text-sm font-semibold text-slate-900">Key positions</div>
            <div className="text-xs text-slate-500">
              Auto-detected by Eleetz · {phases.length} phases across the movement
            </div>
          </div>
        </div>

        {/* track */}
        <div className="relative pt-3 pb-1">
          <div className="h-1.5 rounded-full bg-slate-100" />
          <div className="absolute inset-x-0 top-2.5">
            {phases.map((p) => (
              <div key={p.id} className="absolute -translate-x-1/2" style={{ left: `${p.pct}%` }}>
                <span className="block h-3 w-3 rounded-full bg-el-500 ring-2 ring-el-200" />
                <div className="mt-2 text-[0.65rem] font-semibold text-el-700 text-center -translate-x-1/2 absolute left-1/2">
                  {p.id}
                </div>
              </div>
            ))}
          </div>
        </div>

        <ul className="mt-10 grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-2">
          {phases.map((p) => (
            <li key={p.id} className="rounded-md bg-slate-50 border border-slate-100 px-3 py-2.5">
              <div className="flex items-center gap-2">
                <span className="text-[0.65rem] font-semibold text-el-700">{p.id}</span>
                <span className="text-sm font-medium text-slate-900 truncate">{p.label}</span>
                <span className="ml-auto text-[0.65rem] text-slate-400">at {p.pct}%</span>
              </div>
              {p.cue && (
                <div className="text-[0.7rem] text-slate-500 mt-1">{p.cue}</div>
              )}
            </li>
          ))}
        </ul>
      </div>

      {/* Body measures + camera */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <div className="card p-5 lg:col-span-2">
          <div className="flex items-center gap-2 mb-3">
            <Sparkles className="h-4 w-4 text-el-600" />
            <div className="text-sm font-semibold text-slate-900">Body measures captured</div>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
            {metrics.map((m) => (
              <div key={m.key} className="rounded-md bg-slate-50 border border-slate-100 px-3 py-2.5">
                <div className="text-sm font-medium text-slate-900">{m.label}</div>
                <div className="text-[0.7rem] text-slate-500">measured in {m.unit}</div>
              </div>
            ))}
          </div>
        </div>

        <div className="card p-5">
          <div className="flex items-center gap-2 mb-3">
            <Camera className="h-4 w-4 text-el-600" />
            <div className="text-sm font-semibold text-slate-900">Recommended camera setup</div>
          </div>
          {setups.length === 0 ? (
            <div className="text-xs text-slate-500">No specific guide for this template yet.</div>
          ) : (
            <ul className="space-y-3">
              {setups.map((s, i) => (
                <li key={i} className="rounded-md bg-slate-50 border border-slate-100 p-3">
                  <div className="flex items-center gap-2 mb-1">
                    <span className="text-sm font-medium text-slate-900">{s.angle}</span>
                    <span className="ml-auto text-[0.7rem] text-slate-500">
                      {s.distanceM} m · {s.heightM} m
                    </span>
                  </div>
                  <p className="text-[0.7rem] text-slate-600">{s.notes}</p>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>

      {/* Drills */}
      <div className="card p-5">
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2">
            <Dumbbell className="h-4 w-4 text-el-600" />
            <div className="text-sm font-semibold text-slate-900">Drills tied to phase issues</div>
          </div>
          <Link to="/sports" className="text-xs font-medium text-el-700 hover:text-el-800">
            All drills →
          </Link>
        </div>
        {drills.length === 0 ? (
          <div className="text-xs text-slate-500">No drills available yet for this template.</div>
        ) : (
          <ul className="divide-y divide-slate-100">
            {drills.map((d) => (
              <li key={d.id} className="py-3 flex items-start gap-4">
                {d.phase ? (
                  <span className="badge bg-el-50 text-el-700 ring-1 ring-el-200 w-16 justify-center shrink-0">
                    {d.phase}
                  </span>
                ) : (
                  <span className="w-16 shrink-0" />
                )}
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <div className="text-sm font-semibold text-slate-900">{d.label}</div>
                    <span className="badge bg-slate-100 text-slate-600 ring-1 ring-slate-200">
                      {d.difficulty}
                    </span>
                    <span className="text-[0.7rem] text-slate-500 inline-flex items-center gap-1">
                      <Clock className="h-3 w-3" />
                      {d.duration}
                    </span>
                  </div>
                  <div className="text-xs text-slate-600 mt-1">
                    <span className="text-slate-700 font-medium">Goal:</span> {d.goal}
                  </div>
                  <div className="text-xs text-slate-600 mt-0.5">
                    <span className="text-el-700 font-medium">Cue:</span> {d.cue}
                  </div>
                </div>
                <button className="btn-secondary !h-8 !text-xs shrink-0">
                  <Send className="h-3.5 w-3.5" /> Send to roster
                </button>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}
